// First-sign-in gates (plan 11.4.4, 7.7, 11.3; BUILD-SPEC-api §1.2; mockup login.js gateCard): after the password the
// server lists the gates still open in GET /auth/me `gates` — password_change → policy_ack → mfa_enroll → consent. This
// card shows them in the order the server gives, with the mockup's step list ("처음 로그인 · i/n단계"). After each step it
// asks the server again and goes on until no gate is left; only then does the shell open. There is no close button:
// a step cannot be skipped, only the whole sign-in can be left ("다른 계정으로 로그인").
import { RECORD_LEVELS, orderGates, type Gate, type RecordLevel } from '@aiax-hub/contracts';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { authKeys, changePassword, fetchMe, logout, pendingGates } from '../../api/auth';
import { ApiError } from '../../api/client';
import type { AuthMe } from '../../api/types';
import { useFmt, useHasT, useLang, useT } from '../../i18n';
import { cls } from '../../lib/util';
import { copyWithToast } from '../../ui/CodeBox';
import { Icon } from '../../ui/Icon';
import { Md } from '../../ui/Md';
import { Alert, ErrorState, Skeleton } from '../../ui/States';
import { toast } from '../../ui/layers';
import { ackPledge, agreeNotice, chooseLevel, enrollMfa, fetchConsent, fetchNotice, fetchPledge, gateKeys, verifyMfa, type MfaSecret } from './api';
import { AuthCard, CardTitle, PasswordRules, PwField, Spin, isCode, isLockError, lockedUntilOf, pwRulesOk } from './parts';

/** Step names of the mockup wording (login.gate.s.<name>, login.gate.<name>.title/desc). */
const STEP_KEY: Record<Gate, string> = { password_change: 'password', policy_ack: 'pledge', mfa_enroll: 'mfaSetup', consent: 'consent' };

const uniq = <T,>(list: readonly T[]): T[] => list.filter((x, i) => list.indexOf(x) === i);
const focusTitle = () => requestAnimationFrame(() => document.getElementById('lg-title')?.focus());
const is401 = (e: unknown): boolean => e instanceof ApiError && e.status === 401;
/** A gated session that ended (401 while loading a step) goes back to the sign-in form. */
function useSignedOutOn(error: unknown, onSignedOut: () => void) {
  const fire = is401(error);
  useEffect(() => {
    if (fire) onSignedOut();
  }, [fire]);
}

interface StepProps {
  onDone: () => void;
  onSignedOut: () => void;
}

export interface GateFlowProps {
  /** Gates the sign-in answer already named (password_change_required) before /auth/me answers. */
  first?: readonly Gate[];
  /** The temporary password typed at sign-in: the password step sends it again as `current`. */
  tempPw: string | null;
  /** Typed user id — rule check fallback until /auth/me answers. */
  loginId: string;
  onDone: (me: AuthMe) => void;
  /** The gated session ended (401). */
  onSignedOut: () => void;
  /** The person signed out on purpose ("다른 계정으로 로그인"). */
  onLeave: () => void;
  onLocked: (until: number) => void;
}

export function GateFlow({ first, tempPw, loginId, onDone, onSignedOut, onLeave, onLocked }: GateFlowProps) {
  const t = useT();
  const hasT = useHasT();
  const qc = useQueryClient();
  const [me, setMe] = useState<AuthMe | null>(null);
  const [steps, setSteps] = useState<Gate[]>(() => orderGates(first ?? []));
  const [remaining, setRemaining] = useState<Gate[] | null>(null);
  const [loadErr, setLoadErr] = useState<ApiError | null>(null);
  const [leaving, setLeaving] = useState(false);
  const done = useRef<Gate[]>([]);

  async function refresh(completed?: Gate) {
    if (completed) done.current = uniq([...done.current, completed]);
    setLoadErr(null);
    let fresh: AuthMe | null;
    try {
      fresh = await qc.fetchQuery({ queryKey: authKeys.me, queryFn: ({ signal }) => fetchMe(signal), staleTime: 0 });
    } catch (e) {
      setLoadErr(e instanceof ApiError ? e : new ApiError(0, 'NETWORK', 'me'));
      return;
    }
    if (!fresh) {
      onSignedOut();
      return;
    }
    setMe(fresh);
    const server = pendingGates(fresh);
    // before any step the gates the sign-in already reported stay in front (the server may list them a moment later)
    const known = completed ? [] : (first ?? []).filter((g) => !server.includes(g));
    const rest = orderGates([...known, ...server]).filter((g) => !done.current.includes(g));
    setSteps((prev) => orderGates([...prev, ...rest]));
    if (!rest.length) {
      onDone(fresh);
      return;
    }
    setRemaining(rest);
    if (completed) focusTitle();
  }

  useEffect(() => {
    void refresh();
    // once on arrival; later refreshes follow the steps
  }, []);

  const signOut = async () => {
    setLeaving(true);
    try {
      await logout();
    } catch {
      /* the gated session may already be gone */
    }
    qc.setQueryData(authKeys.me, null);
    onLeave();
  };

  const current = remaining?.[0] ?? null;
  const roleId = me?.primaryRole ?? 'member';
  const role = hasT('role.' + roleId) ? t('role.' + roleId) : roleId;
  const name = me?.user.displayName ?? '';

  let body: ReactNode;
  if (loadErr) body = <ErrorState title={t('state.failTitle')} desc={t('login.gate.errLoad')} code={loadErr.requestId ?? null} onRetry={() => void refresh()} />;
  else if (!current) body = <Skeleton lines={5} />;
  else {
    const stepProps: StepProps = { onDone: () => void refresh(current), onSignedOut };
    if (current === 'password_change') body = <PasswordStep {...stepProps} tempPw={tempPw} loginId={me?.user.loginId || loginId} onLocked={onLocked} />;
    else if (current === 'policy_ack') body = <PledgeStep {...stepProps} name={name} />;
    else if (current === 'mfa_enroll') body = <MfaStep {...stepProps} />;
    else body = <ConsentStep {...stepProps} />;
  }

  const idx = current ? steps.indexOf(current) : -1;
  const key = current ? STEP_KEY[current] : null;
  return (
    <AuthCard variant="lg-card--gate">
      {current && idx >= 0 ? (
        <>
          <p className="lg-kicker">{t('login.gate.kicker', { i: idx + 1, n: steps.length })}</p>
          <ol className="flow-steps lg-steps" aria-label={t('login.gate.stepsLabel')}>
            {steps.map((g, i) => {
              const isDone = done.current.includes(g) || i < idx;
              const isCur = g === current;
              return (
                <li key={g} className={isDone ? 'is-done' : isCur ? 'is-current' : undefined} aria-current={isCur ? 'step' : undefined}>
                  <b>{isDone ? <Icon name="check" /> : String(i + 1)}</b>
                  {t('login.gate.s.' + STEP_KEY[g])}
                </li>
              );
            })}
          </ol>
          <CardTitle>{t(`login.gate.${key}.title`)}</CardTitle>
          <p className="auth-card__sub">{t(`login.gate.${key}.desc`, { role, name })}</p>
        </>
      ) : (
        <CardTitle>{t('login.gate.loading')}</CardTitle>
      )}
      {body}
      <p className="auth-card__note lg-noskip">
        <Icon name="lock" />
        <span>{t('login.gate.noClose')}</span>
      </p>
      <button className="btn btn--ghost btn--block lg-back" type="button" disabled={leaving} onClick={() => void signOut()}>
        <Icon name="chevL" />
        {t('login.mfa.other')}
      </button>
    </AuthCard>
  );
}

// ───────────────────────── 1) temporary password ─────────────────────────
function PasswordStep({ tempPw, loginId, onDone, onSignedOut, onLocked }: StepProps & { tempPw: string | null; loginId: string; onLocked: (until: number) => void }) {
  const t = useT();
  const [needCurrent, setNeedCurrent] = useState(!tempPw);
  const [g, setG] = useState({ p0: '', p1: '', p2: '', err: null as string | null });
  const [busy, setBusy] = useState(false);
  const focus = (sel: string) => requestAnimationFrame(() => document.querySelector<HTMLElement>(sel)?.focus());

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const current = needCurrent ? g.p0 : tempPw;
    if (!current) {
      setG((x) => ({ ...x, err: t('login.err.pw') }));
      focus('#lg-gp0');
      return;
    }
    if (!pwRulesOk(g.p1, g.p2, loginId)) {
      setG((x) => ({ ...x, err: t('login.gate.pw.err') }));
      focus('#lg-gp1');
      return;
    }
    setBusy(true);
    try {
      await changePassword({ current, next: g.p1 });
      toast(t('login.gate.pw.done'), 'ok');
      onDone();
    } catch (err) {
      if (err instanceof ApiError && isLockError(err)) onLocked(lockedUntilOf(err));
      else if (isCode(err, 'session_expired') || isCode(err, 'unauthenticated')) onSignedOut();
      else if (is401(err)) {
        // invalid_credentials: the current (temporary) password did not match — ask for it
        setNeedCurrent(true);
        setG((x) => ({ ...x, p0: '', err: t('login.gate.pw.errCurrent') }));
        focus('#lg-gp0');
      } else if (err instanceof ApiError && err.status >= 400 && err.status < 500) {
        setG((x) => ({ ...x, err: t('login.gate.pw.errServer') }));
        focus('#lg-gp1');
      } else setG((x) => ({ ...x, err: t('login.err.server') }));
    } finally {
      setBusy(false);
    }
  }

  const described = 'lg-gp-rules' + (g.err ? ' lg-gp-err' : '');
  return (
    <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={submit}>
      {needCurrent ? <PwField id="lg-gp0" label={t('login.gate.pw.current')} value={g.p0} autoComplete="current-password" onChange={(v) => setG((x) => ({ ...x, p0: v }))} invalid={!!g.err && !g.p0} /> : null}
      <PwField id="lg-gp1" label={t('login.gate.pw.new')} value={g.p1} onChange={(v) => setG((x) => ({ ...x, p1: v }))} invalid={!!g.err} describedBy={described} />
      <PwField id="lg-gp2" label={t('login.gate.pw.confirm')} value={g.p2} onChange={(v) => setG((x) => ({ ...x, p2: v }))} invalid={!!g.err} describedBy={described} />
      <PasswordRules id="lg-gp-rules" p1={g.p1} p2={g.p2} loginId={loginId} />
      {g.err ? (
        <p className="field__error" id="lg-gp-err" role="alert">
          {g.err}
        </p>
      ) : null}
      <SubmitButton busy={busy}>{t('login.gate.pw.submit')}</SubmitButton>
    </form>
  );
}

function SubmitButton({ busy, disabled, children }: { busy: boolean; disabled?: boolean; children: ReactNode }) {
  return (
    <button className={cls('btn btn--primary btn--lg btn--block', busy && 'is-busy')} type="submit" disabled={disabled} aria-disabled={busy || undefined}>
      {busy ? <Spin /> : null}
      {children}
    </button>
  );
}

// ───────────────────────── 2) prohibited-use pledge (plan 7.7) ─────────────────────────
const norm = (s: string) => s.replace(/\s+/g, '').toLowerCase();

function PledgeStep({ name, onDone, onSignedOut }: StepProps & { name: string }) {
  const t = useT();
  const lang = useLang();
  const q = useQuery({ queryKey: gateKeys.pledge, queryFn: ({ signal }) => fetchPledge(signal), staleTime: 0 });
  const [ok, setOk] = useState(false);
  const [sign, setSign] = useState('');
  const [err, setErr] = useState<{ check?: string; sign?: string; server?: string }>({});
  const [busy, setBusy] = useState(false);
  const focus = (sel: string) => requestAnimationFrame(() => document.querySelector<HTMLElement>(sel)?.focus());
  useSignedOutOn(q.error, onSignedOut);

  if (q.isPending || is401(q.error)) return <Skeleton lines={5} />;
  if (q.isError) return <ErrorState title={t('state.failTitle')} desc={t('login.gate.errLoad')} code={q.error instanceof ApiError ? (q.error.requestId ?? null) : null} onRetry={() => void q.refetch()} />;
  const pledge = q.data;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    const next: typeof err = {};
    if (!ok) next.check = t('login.gate.pledge.errCheck');
    if (!sign.trim() || (name && norm(sign) !== norm(name))) next.sign = t('login.gate.pledge.errSign');
    setErr(next);
    if (next.check || next.sign) {
      focus(next.check ? '#lg-pl-ok' : '#lg-pl-sign');
      return;
    }
    setBusy(true);
    try {
      await ackPledge(pledge, sign.trim());
      toast(t('login.gate.pledge.done'), 'ok');
      onDone();
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 401) onSignedOut();
      else if (e2 instanceof ApiError && e2.status === 409) {
        setErr({ server: t('login.gate.pledge.changed') });
        setOk(false);
        void q.refetch();
      } else if (e2 instanceof ApiError && e2.status === 422) {
        setErr({ sign: t('login.gate.pledge.errSign') });
        focus('#lg-pl-sign');
      } else setErr({ server: t('login.gate.pledge.errServer') });
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={submit}>
      <ol className="lg-pledge">
        {pledge.statements.map((s, i) => (
          <li key={i}>{s[lang]}</li>
        ))}
      </ol>
      <div className="field">
        <label className="lg-check" htmlFor="lg-pl-ok">
          <input type="checkbox" id="lg-pl-ok" checked={ok} aria-invalid={err.check ? true : undefined} aria-describedby={err.check ? 'lg-pl-err1' : undefined} onChange={(e) => setOk(e.target.checked)} />
          <span>{t('login.gate.pledge.check')}</span>
        </label>
        {err.check ? (
          <p className="field__error" id="lg-pl-err1" role="alert">
            {err.check}
          </p>
        ) : null}
      </div>
      <div className="field">
        <label className="field__label" htmlFor="lg-pl-sign">
          {name ? t('login.gate.pledge.sign', { name }) : t('login.gate.pledge.signPlain')}
        </label>
        <input
          className="input lg-input"
          id="lg-pl-sign"
          type="text"
          autoComplete="name"
          value={sign}
          placeholder={name || undefined}
          aria-invalid={err.sign ? true : undefined}
          aria-describedby={err.sign ? 'lg-pl-err2' : undefined}
          onChange={(e) => setSign(e.target.value)}
        />
        {err.sign ? (
          <p className="field__error" id="lg-pl-err2" role="alert">
            {err.sign}
          </p>
        ) : null}
      </div>
      <p className="field__hint">{t('login.gate.pledge.versionV', { v: pledge.version })}</p>
      {err.server ? <Alert tone="danger" title={err.server} /> : null}
      <SubmitButton busy={busy}>{t('login.gate.pledge.submit')}</SubmitButton>
    </form>
  );
}

// ───────────────────────── 3) MFA enrolment (plan 11.4.3) ─────────────────────────
/** "JBSW Y3DP …": the setup key in groups of four for reading and typing. */
export const groupKey = (secret: string) => secret.match(/.{1,4}/g)?.join(' ') ?? secret;

/** Enrol once on arrival (the secret is shown once), verify a 6-digit code. Shared with the settings screen. */
export function useMfaEnrollment(onSignedOut?: () => void) {
  const [secret, setSecret] = useState<MfaSecret | null>(null);
  const [failed, setFailed] = useState<ApiError | null>(null);
  const started = useRef(false);
  const start = async () => {
    setFailed(null);
    try {
      setSecret(await enrollMfa());
    } catch (e) {
      if (e instanceof ApiError && e.status === 401 && onSignedOut) onSignedOut();
      else setFailed(e instanceof ApiError ? e : new ApiError(0, 'NETWORK', 'enroll'));
    }
  };
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void start();
  }, []);
  return { secret, failed, retry: () => void start() };
}

/** Local QR rendering keeps the enrolment secret off external image services. Shared by both enrolment screens. */
export function MfaKeyBox({ secret, className }: { secret: MfaSecret; className?: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  return (
    <div className="mfa-setup">
      <figure className="mfa-setup__qr">
        <QRCodeSVG
          value={secret.otpauthUri}
          size={224}
          level="M"
          marginSize={4}
          bgColor="#ffffff"
          fgColor="#000000"
          title={t('login.gate.mfa.qrLabel')}
          aria-label={t('login.gate.mfa.qrLabel')}
        />
        <figcaption className="field__hint">{t('login.gate.mfa.qrHint')}</figcaption>
      </figure>
      <p className="field__hint">{t('login.gate.mfa.apps')}</p>
      <div className="field">
        <p className="field__hint">{t('login.gate.mfa.manualHint')}</p>
        <p className="field__label">{t('login.gate.mfa.keyLabel')}</p>
        <div className={cls('cmd-box', className, copied && 'is-copied')}>
          <code tabIndex={0} aria-label={t('login.gate.mfa.keyLabel')}>
            {groupKey(secret.secret)}
          </code>
          <button
            className="cmd-box__copy"
            type="button"
            aria-label={t('login.gate.mfa.copy')}
            data-tip={t('login.gate.mfa.copy')}
            onClick={async () => {
              if (await copyWithToast(secret.secret, t)) {
                setCopied(true);
                setTimeout(() => setCopied(false), 1600);
              }
            }}
          >
            <Icon name={copied ? 'check' : 'copy'} />
          </button>
        </div>
        <p className="field__hint">
          {t('login.gate.mfa.keyHint')}{' '}
          <a className="link-btn" href={secret.otpauthUri}>
            {t('login.gate.mfa.openApp')}
          </a>
        </p>
      </div>
    </div>
  );
}

/** 6-digit code field (mockup codeField). */
export function CodeField({ id, value, onChange, error, className }: { id: string; value: string; onChange: (v: string) => void; error: string | null; className?: string }) {
  const t = useT();
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        {t('login.mfa.code')}
      </label>
      <input
        className={cls('input', className)}
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={6}
        pattern="[0-9]*"
        placeholder="000000"
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={`${id}-hint` + (error ? ` ${id}-err` : '')}
        onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
      />
      <p className="field__hint" id={`${id}-hint`}>
        {t('login.mfa.hint')}
      </p>
      {error ? (
        <p className="field__error" id={`${id}-err`} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function MfaStep({ onDone, onSignedOut }: StepProps) {
  const t = useT();
  const { secret, failed, retry } = useMfaEnrollment(onSignedOut);
  const [code, setCode] = useState('');
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const focusCode = () => requestAnimationFrame(() => document.getElementById('lg-gm-code')?.focus());

  if (failed) return <ErrorState title={t('state.failTitle')} desc={t('login.gate.mfa.errEnroll')} code={failed.requestId ?? null} onRetry={retry} />;
  if (!secret) return <Skeleton lines={4} />;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!/^\d{6}$/.test(code)) {
      setErr(t('login.mfa.err'));
      focusCode();
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await verifyMfa(code);
      toast(t('login.gate.mfa.done'), 'ok');
      onDone();
    } catch (e2) {
      if (isCode(e2, 'invalid_code') || (e2 instanceof ApiError && (e2.status === 400 || e2.status === 422))) {
        setErr(t('login.mfa.errWrong'));
        setCode('');
        focusCode();
      } else if (e2 instanceof ApiError && e2.status === 401) onSignedOut();
      else setErr(t('login.err.server'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={submit}>
      <MfaKeyBox secret={secret} className="lg-key" />
      <CodeField id="lg-gm-code" value={code} onChange={setCode} error={err} className="lg-input lg-code" />
      <SubmitButton busy={busy}>{t('login.gate.mfa.submit')}</SubmitButton>
    </form>
  );
}

// ───────────────────────── 4) notice consent + recording level (plan 11.3.1, 11.3.5) ─────────────────────────
function ConsentStep({ onDone, onSignedOut }: StepProps) {
  const t = useT();
  const lang = useLang();
  const fmt = useFmt();
  const notice = useQuery({ queryKey: gateKeys.notice, queryFn: ({ signal }) => fetchNotice(signal), staleTime: 0 });
  const consent = useQuery({ queryKey: gateKeys.consent, queryFn: ({ signal }) => fetchConsent(signal), staleTime: 0 });
  const [read, setRead] = useState(false);
  const [level, setLevel] = useState<RecordLevel>(1);
  const [ack4, setAck4] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLParagraphElement>(null);
  const ready = !!notice.data && !!consent.data;

  // "끝까지 읽으면 동의할 수 있어요": the end of the notice must come into view (no observer → nothing to wait for)
  useEffect(() => {
    if (!ready || read) return;
    const end = endRef.current;
    if (!end || typeof IntersectionObserver === 'undefined') {
      setRead(true);
      return;
    }
    const io = new IntersectionObserver((es) => {
      if (es.some((x) => x.isIntersecting)) {
        setRead(true);
        io.disconnect();
      }
    }, { root: boxRef.current, threshold: 0.6 });
    io.observe(end);
    return () => io.disconnect();
  }, [ready, read, notice.data?.hash]);

  const failed = notice.error ?? consent.error;
  useSignedOutOn(failed, onSignedOut);
  if (is401(failed)) return <Skeleton lines={6} />;
  if (failed)
    return (
      <ErrorState
        title={t('state.failTitle')}
        desc={t('login.gate.errLoad')}
        code={failed instanceof ApiError ? (failed.requestId ?? null) : null}
        onRetry={() => {
          void notice.refetch();
          void consent.refetch();
        }}
      />
    );
  if (!notice.data || !consent.data) return <Skeleton lines={6} />;
  const n = notice.data;
  const cap = consent.data.cap.maxLevel;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy) return;
    if (!read) {
      setErr(t('settings.n.scroll'));
      return;
    }
    if (level === 4 && !ack4) {
      setErr(t('settings.lv.c4.need'));
      requestAnimationFrame(() => document.getElementById('lg-c4')?.focus());
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await agreeNotice(n);
      if (level > 1) await chooseLevel(level, level === 4);
      toast(t('login.gate.consent.done', { level: t('level.' + level) }), 'ok');
      onDone();
    } catch (e2) {
      if (e2 instanceof ApiError && e2.status === 401) onSignedOut();
      else if (isCode(e2, 'notice_changed')) {
        setRead(false);
        setErr(t('login.gate.consent.changed'));
        void notice.refetch();
        void consent.refetch();
      } else setErr(t('login.gate.consent.errServer'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="form lg-form" noValidate aria-busy={busy || undefined} onSubmit={submit}>
      <div className="lg-notice" ref={boxRef} tabIndex={0} role="region" aria-label={t('settings.n.title')}>
        <p className="lg-notice__ver">{t('settings.n.kicker', { v: n.version, date: fmt.date(n.publishedAt) })}</p>
        {n.changes ? (
          <div className="lg-notice__changes">
            <b>{t('settings.n.changes')}</b>
            <p>{n.changes[lang]}</p>
          </div>
        ) : null}
        {n.sections.map((s) => (
          <section key={s.n} className="lg-notice__sec">
            <h2>{t('admin.ns.' + s.n)}</h2>
            <Md src={s.body[lang]} wrap />
          </section>
        ))}
        <p className="lg-notice__end" ref={endRef}>
          <Icon name="check" />
          {t('settings.n.end')}
        </p>
      </div>
      <p className="field__hint" aria-live="polite">
        {read ? t('settings.n.hash', { hash: n.hash }) : t('settings.n.scroll')}
      </p>
      <fieldset className="lg-levels">
        <legend className="field__label">{t('login.gate.consent.level')}</legend>
        <div className="radio-cards" role="radiogroup" aria-label={t('settings.lv.pick')}>
          {RECORD_LEVELS.map((lv) => (
            <label key={lv} className={cls('radio-card', lv > cap && 'is-above')} htmlFor={`lg-lv-${lv}`}>
              <input type="radio" id={`lg-lv-${lv}`} name="lg-level" value={lv} checked={level === lv} disabled={lv > cap} onChange={() => setLevel(lv)} />
              <span>
                <b>{t('level.' + lv)}</b>
                <small>{lv > cap ? t('settings.lv.capAbove') : t('settings.lv.add.' + lv)}</small>
              </span>
            </label>
          ))}
        </div>
        <p className="field__hint">{t('login.gate.consent.levelHint')}</p>
      </fieldset>
      {level === 4 ? (
        <label className="lg-check" htmlFor="lg-c4">
          <input type="checkbox" id="lg-c4" checked={ack4} onChange={(e) => setAck4(e.target.checked)} />
          <span>{t('settings.lv.c4.check')}</span>
        </label>
      ) : null}
      {err ? (
        <p className="field__error" role="alert">
          {err}
        </p>
      ) : null}
      <SubmitButton busy={busy} disabled={!read}>
        {t('login.gate.consent.submit')}
      </SubmitButton>
    </form>
  );
}
